import React, { useCallback, useContext } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import FormatCurrency from 'components/Common/FormatCurrency'
import SolidExclamationCircleIcon from 'components/Luxkit/Icons/solid/SolidExclamationCircleIcon'
import Pill from 'components/Luxkit/Pill'

import { pluralizeToString } from 'lib/pluralize'
import TaxesAndFees from 'components/OfferPage/PackageOption/Pricing/TaxesAndFees'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL, OFFER_TYPE_LAST_MINUTE } from 'constants/offer'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import getValueOffPercent from 'lib/getValueOffPercent'
import Caption from 'components/Luxkit/Typography/Caption'
import Heading from 'components/Luxkit/Typography/Heading'
import useHotelPriceColour from 'hooks/useHotelPriceColour'
import GeoContext from 'contexts/geoContext'
import getLPCRegionLOS from 'lib/getLPCRegionLOS'
import Group from 'components/utils/Group'
import TextButton from 'components/Luxkit/Button/TextButton'
import PaletteProvider from 'contexts/PaletteProvider'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { ValueWrapper } from 'components/Common/OfferPricing/styles'

export const StyledExclamationIcon = styled(SolidExclamationCircleIcon)`
  position: relative;
  margin-left: ${rem(5)};
  top: ${rem(3)};
`

interface Props {
  offer: App.Offer | App.OfferSummary;
  pkg: App.Package;
}

function HeroOfferTilePricing(props: Props) {
  const {
    offer,
    pkg,
  } = props

  const { showTaxExclusivePrices } = useContext(GeoContext)
  const taxesAndFees = pkg.taxesAndFees
  const propertyFees = pkg.propertyFees ?? 0
  const exclusivePriceColor = useHotelPriceColour(taxesAndFees)

  const totalPrice = showTaxExclusivePrices ? pkg.price : pkg.price + propertyFees
  const totalValue = showTaxExclusivePrices ? pkg.value : pkg.value + propertyFees

  const valueOffPercent = getValueOffPercent(totalValue, totalPrice)
  const shouldShowRate = valueOffPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE

  const shouldShowDiscountPercent = offer.type === OFFER_TYPE_LAST_MINUTE
  const shouldShowValue = pkg.value > 0 && totalValue > totalPrice && pkg.shouldDisplayValue &&
    !shouldShowDiscountPercent && (offer.type == OFFER_TYPE_HOTEL || !pkg.roomRate?.inclusionsHideValue)

  let staysFrom = offer.minDuration
  const LPCRegionLOS = getLPCRegionLOS(offer)
  if (LPCRegionLOS && offer.packages.some(p => p.duration === LPCRegionLOS)) {
    staysFrom = LPCRegionLOS
  }
  const showStaysFromLabel = staysFrom && offer.type === OFFER_TYPE_ALWAYS_ON && LPCRegionLOS && staysFrom < pkg.duration

  const getDurationText = useCallback(() => {
    if (offer.tileDurationLabel) {
      return `${offer.tileDurationLabel} from`
    }
    if (LPCRegionLOS) {
      return `${pkg.duration} nights from`
    }
    return 'From'
  }, [LPCRegionLOS, offer.tileDurationLabel, pkg.duration])

  return (
    <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
      <Group direction="vertical">
        <Caption variant="medium" colour="neutral-two">
          {getDurationText()}
        </Caption>
        <Heading data-testid="search-price" variant="heading3" colour={exclusivePriceColor}>
          <FormatCurrency value={totalPrice} format="roundedDollar" />
          <Caption variant="medium" as="span" colour="neutral-two">/{offer.saleUnit}</Caption>
        </Heading>
        {shouldShowValue && (
          <ValueWrapper variant="large" weight="bold" colour="neutral-one">
            Valued up to <FormatCurrency value={Math.ceil(totalValue)} />
            {shouldShowRate &&
              <PaletteProvider type="default">
                <Pill kind="primary" variant="success">-{valueOffPercent}%</Pill>
              </PaletteProvider>
            }
          </ValueWrapper>
        )}
        <TaxesAndFees
          taxesPayableAtProperty={offer.property?.taxesPayableAtProperty}
          taxAmount={taxesAndFees}
        />
        {staysFrom && showStaysFromLabel &&
          <Caption colour="neutral-three" variant="medium">
            Stays from {pluralizeToString('night', staysFrom)} available
          </Caption>
        }
        {!!pkg.roomOccupancy && offer.type === 'tour' && (
          <Caption colour="neutral-two" variant="medium">
            {pkg.roomOccupancy}
          </Caption>
        )}
      </Group>
      <CSSBreakpoint min="md">
        <TextButton kind="primary">
          View offer
        </TextButton>
      </CSSBreakpoint>
    </Group>
  )
}

export default HeroOfferTilePricing
